/** @jsxImportSource @emotion/react */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react'
import { App } from 'antd'
import req from '@sportninja/common/api/request'
import { saveVenue, updateVenue } from 'src/helpers/EntityCreationHelper'

// Types
export type Facility = {
  id?: string
  name: string
  isNew?: boolean
}

export type VenueData = {
  id?: string
  name?: string
  address_id?: string
  address_line1?: string
  address_line2?: string
  city?: string
  postcode?: string
  country_id?: number
  province_id?: number
  country?: string
  province?: string
  facilities?: Facility[]
}

type CustomError = {
  name: string | null
  address: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  postcode: string | null
  country: string | null
  province: string | null
}

type LocationState = {
  // ID (for edit mode)
  id: string | null

  // Form fields
  name: string
  address_line1: string
  address_line2: string
  city: string
  postcode: string
  countryIsoCode: string
  provinceIsoCode: string
  countryId: number | null
  provinceId: number | null
  provinceName: string | null

  // Data
  facilities: Facility[]
  deletedFacilityIds: string[]

  // UI state
  isLoading: boolean
  error: string | null
  fieldErrors: string[]
  customError: CustomError
  showNewFacilityInput: boolean
  newFacilityName: string
  editingFacilityId: string | null
  editingFacilityName: string
}

type LocationAction =
  | { type: 'SET_FIELD'; field: keyof LocationState; value: unknown }
  | { type: 'SET_COUNTRY'; isoCode: string; id: number | null }
  | { type: 'SET_PROVINCE'; isoCode: string; id: number | null }
  | { type: 'ADD_FACILITY'; name: string }
  | { type: 'UPDATE_FACILITY'; id: string; name: string }
  | { type: 'DELETE_FACILITY'; id: string }
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_FIELD_ERRORS'; errors: string[] }
  | { type: 'SET_CUSTOM_ERROR_BY_FIELD'; field: string; message: string | null }
  | { type: 'CLEAR_CUSTOM_ERROR_BY_FIELD'; field: string }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'TOGGLE_NEW_FACILITY_INPUT' }
  | { type: 'SET_NEW_FACILITY_NAME'; name: string }
  | { type: 'START_EDITING_FACILITY'; id: string; name: string }
  | { type: 'CANCEL_EDITING_FACILITY' }
  | { type: 'SET_EDITING_FACILITY_NAME'; name: string }
  | { type: 'RESET_FORM' }
  | { type: 'INIT_EDIT_MODE'; data: VenueData; facilities: Facility[] }

const initialCustomError: CustomError = {
  name: null,
  address: null,
  address_line1: null,
  address_line2: null,
  city: null,
  postcode: null,
  country: null,
  province: null,
}

const initialState: LocationState = {
  id: null,
  name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postcode: '',
  countryIsoCode: '',
  provinceIsoCode: '',
  countryId: null,
  provinceId: null,
  provinceName: '',
  facilities: [],
  deletedFacilityIds: [],
  isLoading: false,
  error: null,
  fieldErrors: [],
  customError: initialCustomError,
  showNewFacilityInput: false,
  newFacilityName: '',
  editingFacilityId: null,
  editingFacilityName: '',
}

function locationReducer(
  state: LocationState,
  action: LocationAction
): LocationState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }

    case 'SET_COUNTRY':
      return {
        ...state,
        countryIsoCode: action.isoCode,
        countryId: action.id,
        // Clear province when country changes
        provinceIsoCode: '',
        provinceId: null,
      }

    case 'SET_PROVINCE':
      return {
        ...state,
        provinceIsoCode: action.isoCode,
        provinceId: action.id,
      }

    case 'ADD_FACILITY': {
      const newFacility: Facility = {
        id: `new-${Date.now()}`,
        name: action.name,
        isNew: true,
      }
      return {
        ...state,
        facilities: [...state.facilities, newFacility],
        showNewFacilityInput: false,
        newFacilityName: '',
      }
    }

    case 'UPDATE_FACILITY':
      return {
        ...state,
        facilities: state.facilities.map((f) =>
          f.id === action.id ? { ...f, name: action.name } : f
        ),
        editingFacilityId: null,
        editingFacilityName: '',
      }

    case 'DELETE_FACILITY': {
      const facilityToDelete = state.facilities.find((f) => f.id === action.id)
      const newDeletedIds =
        facilityToDelete && !facilityToDelete.isNew
          ? [...state.deletedFacilityIds, action.id]
          : state.deletedFacilityIds

      return {
        ...state,
        facilities: state.facilities.filter((f) => f.id !== action.id),
        deletedFacilityIds: newDeletedIds,
      }
    }

    case 'SET_LOADING':
      return { ...state, isLoading: action.value }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_FIELD_ERRORS':
      return { ...state, fieldErrors: action.errors }

    case 'SET_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: action.message,
        },
        fieldErrors: action.message
          ? [...new Set([...state.fieldErrors, action.field])]
          : state.fieldErrors,
      }

    case 'CLEAR_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: null,
        },
        fieldErrors: state.fieldErrors.filter((e) => e !== action.field),
      }

    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        error: null,
        fieldErrors: [],
        customError: initialCustomError,
      }

    case 'TOGGLE_NEW_FACILITY_INPUT':
      return {
        ...state,
        showNewFacilityInput: !state.showNewFacilityInput,
        newFacilityName: '',
      }

    case 'SET_NEW_FACILITY_NAME':
      return { ...state, newFacilityName: action.name }

    case 'START_EDITING_FACILITY':
      return {
        ...state,
        editingFacilityId: action.id,
        editingFacilityName: action.name,
      }

    case 'CANCEL_EDITING_FACILITY':
      return {
        ...state,
        editingFacilityId: null,
        editingFacilityName: '',
      }

    case 'SET_EDITING_FACILITY_NAME':
      return { ...state, editingFacilityName: action.name }

    case 'RESET_FORM':
      return { ...initialState }

    case 'INIT_EDIT_MODE':
      return {
        ...state,
        id: action.data.id || null,
        name: action.data.name || '',
        address_line1: action.data.address_line1 || '',
        address_line2: action.data.address_line2 || '',
        city: action.data.city || '',
        postcode: action.data.postcode || '',
        countryId: action.data.country_id || null,
        provinceId: action.data.province_id || null,
        provinceName: action.data.province || '',
        countryIsoCode: action.data.country || '',
        provinceIsoCode: action.data.province || '',
        facilities: action.facilities || [],
        deletedFacilityIds: [],
      }

    default:
      return state
  }
}

// Context type
type LocationContextValue = {
  state: LocationState
  dispatch: React.Dispatch<LocationAction>
  isEdit: boolean
  onSubmit: () => Promise<void>
  onCancel: () => void
  onDelete: () => Promise<void>
  setCustomErroByField: (field: string, message: string | null) => void
  clearCustomErrorByField: (field: string) => void
}

const LocationContext = createContext<LocationContextValue | null>(null)

// Hook to use location context
export const useLocationContext = () => {
  const context = useContext(LocationContext)
  if (!context) {
    throw new Error('useLocationContext must be used within LocationProvider')
  }
  return context
}

// Provider props
type LocationProviderProps = {
  children: ReactNode
  isOpen: boolean
  onClose: () => void
  initialState?: VenueData
  initialFacilities?: Facility[]
  organizationId: string
  onSuccess: () => void
}

export const LocationProvider: React.FC<LocationProviderProps> = ({
  children,
  onClose,
  initialState: initialVenueData,
  initialFacilities,
  organizationId,
  onSuccess,
}) => {
  const [state, dispatch] = useReducer(locationReducer, initialState)
  const { message } = App.useApp()
  const isEdit = Boolean(initialVenueData?.id)

  // Helper functions for error handling
  const setCustomErroByField = useCallback(
    (field: string, lmessage: string | null) => {
      dispatch({ type: 'SET_CUSTOM_ERROR_BY_FIELD', field, message: lmessage })
    },
    []
  )

  const clearCustomErrorByField = useCallback((field: string) => {
    dispatch({ type: 'CLEAR_CUSTOM_ERROR_BY_FIELD', field })
  }, [])

  const setCustomErrors = useCallback(
    (invalid_fields: Record<string, string | string[]>) => {
      // Helper to normalize error to string (backend may return string or array)
      const normalizeError = (error: string | string[]): string => {
        return Array.isArray(error) ? error.join(', ') : error
      }

      // Helper to get field error (checks both direct and address. prefixed)
      const getFieldError = (
        ...keys: string[]
      ): string | string[] | undefined => {
        for (const key of keys) {
          if (invalid_fields[key]) {
            return invalid_fields[key]
          }
          if (invalid_fields[`address.${key}`]) {
            return invalid_fields[`address.${key}`]
          }
        }
        return undefined
      }

      // Handle each field error from backend
      const nameError = getFieldError('name')
      if (nameError) {
        setCustomErroByField('name', normalizeError(nameError))
      }

      // Handle general address error
      const addressError = getFieldError('address')
      if (addressError) {
        setCustomErroByField('address', normalizeError(addressError))
      }

      // Handle street_1 / address_line1
      const street1Error = getFieldError('street_1', 'address_line1')
      if (street1Error) {
        setCustomErroByField('address_line1', normalizeError(street1Error))
      }

      // Handle street_2 / address_line2
      const street2Error = getFieldError('street_2', 'address_line2')
      if (street2Error) {
        setCustomErroByField('address_line2', normalizeError(street2Error))
      }

      // Handle city
      const cityError = getFieldError('city')
      if (cityError) {
        setCustomErroByField('city', normalizeError(cityError))
      }

      // Handle postal_code / postcode
      const postalError = getFieldError('postal_code', 'postcode')
      if (postalError) {
        setCustomErroByField('postcode', normalizeError(postalError))
      }

      // Handle country_id / country
      const countryError = getFieldError('country_id', 'country')
      if (countryError) {
        setCustomErroByField('country', normalizeError(countryError))
      }

      // Handle province_id / province
      const provinceError = getFieldError('province_id', 'province')
      if (provinceError) {
        setCustomErroByField('province', normalizeError(provinceError))
      }
    },
    [setCustomErroByField]
  )

  // Initialize edit mode when data is available
  React.useEffect(() => {
    if (initialVenueData && isEdit) {
      dispatch({
        type: 'INIT_EDIT_MODE',
        data: initialVenueData,
        facilities: initialFacilities || [],
      })
    }
  }, [initialVenueData, initialFacilities, isEdit])

  const onSubmit = useCallback(async () => {
    // Clear previous errors before submitting
    dispatch({ type: 'SET_LOADING', value: true })
    dispatch({ type: 'CLEAR_ALL_ERRORS' })

    try {
      // Prepare facilities for API (include unsaved input if present)
      const allFacilities = [...state.facilities]
      if (state.newFacilityName.trim()) {
        allFacilities.push({
          id: `new-${Date.now()}`,
          name: state.newFacilityName.trim(),
          isNew: true,
        })
      }
      const facilitiesForApi = allFacilities
        .filter((f) => f.name.trim())
        .map((f) => ({
          id: f.isNew ? undefined : f.id,
          name: f.name.trim(),
        }))

      if (isEdit && initialVenueData?.id) {
        // Update existing venue - format data for update
        const venueData = {
          name: state.name.trim(),
          name_full: state.name.trim(),
          address: {
            id: initialVenueData.address_id,
            street_1: state.address_line1.trim(),
            street_2: state.address_line2.trim() || undefined,
            city: state.city.trim(),
            postal_code: state.postcode.trim(),
            country_id: state.countryId ? String(state.countryId) : undefined,
            province_id: state.provinceId
              ? String(state.provinceId)
              : undefined,
            settings: {},
          },
        }
        await updateVenue(
          venueData,
          facilitiesForApi,
          organizationId,
          'organizations',
          {
            id: initialVenueData.id,
            facilities: initialFacilities || [],
          }
        )
        message.success(`${state.name.trim()} updated successfully`)
      } else {
        // Create new venue - format data for creation
        const venueData = {
          name: state.name.trim(),
          name_full: state.name.trim(),
          address: {
            street_1: state.address_line1.trim(),
            street_2: state.address_line2.trim() || undefined,
            city: state.city.trim(),
            postal_code: state.postcode.trim(),
            country_id: state.countryId ? String(state.countryId) : undefined,
            province_id: state.provinceId
              ? String(state.provinceId)
              : undefined,
            settings: {},
            address_type_id: '5',
          },
        }
        await saveVenue(venueData, facilitiesForApi, organizationId)
        message.success(`${state.name.trim()} created successfully`)
      }

      onSuccess()
      onClose()
    } catch (error: unknown) {
      // Handle backend validation errors
      const err = error as {
        invalid_fields?: Record<string, string | string[]>
        message?: string
      }

      if (err.invalid_fields) {
        // Set field-level errors using the same pattern as ScheduleContext
        setCustomErrors(err.invalid_fields)
      } else {
        // General error - display in Alert
        const errorMessage =
          err.message ||
          (error instanceof Error ? error.message : 'An error occurred')
        dispatch({ type: 'SET_ERROR', error: errorMessage })
      }
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [
    state,
    isEdit,
    initialVenueData,
    initialFacilities,
    organizationId,
    onSuccess,
    onClose,
    message,
    setCustomErrors,
  ])

  const onCancel = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
    onClose()
  }, [onClose])

  const onDelete = useCallback(async () => {
    if (!state.id) {
      throw new Error('Cannot delete a location without an ID')
    }

    try {
      await req(`/organizations/${organizationId}/venues/${state.id}`, {
        method: 'DELETE',
      })
      message.success(`${state.name.trim()} removed successfully`)
      onSuccess()
      onClose()
    } catch (error) {
      const err = error as { message?: string }
      console.error('🗑️ [LocationContext] Delete location failed:', err)
      throw error
    }
  }, [state.id, state.name, organizationId, message, onSuccess, onClose])

  const value: LocationContextValue = {
    state,
    dispatch,
    isEdit,
    onSubmit,
    onCancel,
    onDelete,
    setCustomErroByField,
    clearCustomErrorByField,
  }

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  )
}

export default LocationContext
